import { v4 as uuidv4 } from "uuid";
import type { AssetTypeSource } from '../types';
import { ASSET_TYPES_CONFIGS } from "../config/assetTypes";
import { isBackgroundAsset } from '@/pages/editor/components/timeline/TimelineCategoryItem/assetCapabilities';
import { INITIAL_TIMELINE_LENGTH_SECONDS } from '@/pages/editor/components/timeline/utils';
import type { ModelEdits } from '@/modules/library/utils/modelEdits';

export interface AssetToUploadParams {
  file: File;
  assetTypeKey: keyof typeof ASSET_TYPES_CONFIGS;
  assetName?: string;
  userId: string;
  orgInstanceId?: string;
  availability?: string;
  /** Sets asset_type_display_on. Must be true (with is_active) for an asset to
   *  be visible to all users. Defaults to false to preserve existing behaviour. */
  displayOn?: boolean;
  thumbnailUrl?: string;
  audioCategory?: 'dialogue' | 'sfx' | 'music';
  // Attribution metadata for external assets (Sketchfab/Poly Pizza)
  attribution?: string;
  license?: string;
  licenseUrl?: string;
  externalSource?: 'sketchfab' | 'polypizza';
  externalId?: string;
  /** What the user changed on the model, measured on this file; its conversion applies them. */
  edits?: ModelEdits;
}

const getAudioDuration = (file: File): Promise<number> => {
  return new Promise((resolve) => {
    const audio = new Audio();
    const url = URL.createObjectURL(file);

    audio.addEventListener('loadedmetadata', () => {
      URL.revokeObjectURL(url);
      const d = audio.duration;
      resolve(Number.isFinite(d) && d > 0 ? d : 6);
    });

    audio.addEventListener('error', () => {
      URL.revokeObjectURL(url);
      resolve(6);
    });

    audio.src = url;
  });
};

const getVideoDuration = (file: File): Promise<number> => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    const url = URL.createObjectURL(file);

    video.addEventListener('loadedmetadata', () => {
      URL.revokeObjectURL(url);
      const d = video.duration;
      resolve(Number.isFinite(d) && d > 0 ? d : 10);
    });

    video.addEventListener('error', () => {
      URL.revokeObjectURL(url);
      resolve(10); // default 10s for video
    });

    video.src = url;
  });
};

export const createAssetTypeSource = async (
  params: AssetToUploadParams
): Promise<AssetTypeSource> => {
  const { file, assetTypeKey, assetName, userId, orgInstanceId, availability, displayOn, thumbnailUrl, audioCategory, attribution, license, licenseUrl, externalSource, externalId } = params;
  const config = ASSET_TYPES_CONFIGS[assetTypeKey];
  const generatedId = uuidv4();
  
  // Determine end time based on asset type
  let endTime: number;
  if (config.type === 'audio') {
    endTime = await getAudioDuration(file);
  } else if (config.type === 'video') {
    endTime = await getVideoDuration(file);
  } else if (isBackgroundAsset(config.category)) {
    endTime = INITIAL_TIMELINE_LENGTH_SECONDS;
  } else if (config.category === 'character') {
    endTime = 10; // 10 seconds for characters
  } else {
    endTime = 60; // default for regular assets
  }

  // Initialize tags array
  const tags: string[] = [];
  
  // Add audio category tag if provided
  if (audioCategory) {
    const categoryTagMap: Record<string, string> = {
      'dialogue': 'dialogue',
      'sfx': 'sound_effects',
      'music': 'music'
    };
    tags.push(categoryTagMap[audioCategory]);
  }

  // Initialize asset_type_details object
  let assetTypeDetails: Record<string, unknown> = {};
  
  // Add attribution metadata for external assets (Sketchfab/Poly Pizza)
  if (externalSource && externalId) {
    assetTypeDetails = {
      external_source: externalSource,
      external_id: externalId,
      ...(attribution && { attribution }),
      ...(license && { license }),
      ...(licenseUrl && { license_url: licenseUrl }),
      // Flag NC licenses for publish-time checking
      ...(license && license.includes('NC') && { nc: true }),
    };
    console.log('[Stage 4] Attribution metadata being stored:', assetTypeDetails);
  }
  

  // Set asset_type_status to 'uploaded' for all assets - backend will handle processing
  const assetTypeStatus: 'uploaded' | 'processed' = 'uploaded';

  return {
    id: 0, 
    created_at: new Date().toISOString(),
    asset_type_source_name: file.name,
    asset_type_tags: tags,
    asset_type_details: assetTypeDetails,
    asset_type_img_url: thumbnailUrl || '',
    asset_type_category: config.category,
    asset_type_animations: {},
    asset_type_id: generatedId,
    asset_type_display_name: assetName ?? file.name.replace(/\.[^/.]+$/, ''),
    user_id: userId,
    asset_type_position: '0,0,0',
    asset_type_rotation: '0,0,0',
    asset_type_scale: '1,1,1',
    asset_type_is_active: true,
    asset_type_begin_time: 0,
    asset_type_end_time: endTime,
    asset_type_display_on: displayOn ?? false,
    asset_type_source_url: '',
    asset_type_optimized_url: '',
    org_instance_id: orgInstanceId || '',
    asset_type_availability: availability || 'private',
    asset_type_status: assetTypeStatus
  };
};
